import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  Briefcase, Calendar as CalendarIcon, CheckCircle2, ChevronLeft, ChevronRight,
  Clock, DollarSign, Filter, PiggyBank, Plus, Receipt, Search, Sparkles,
  StickyNote, TrendingDown, TrendingUp, Users, X,
} from "lucide-react";
import AppShell from "../components/AppShell";
import IncomeFormModal from "../components/IncomeFormModal";
import ExpenseFormModal from "../components/ExpenseFormModal";
import { api } from "../lib/api";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

// Kind → color / label / short-tag / route
const KIND_META = {
  income:            { label: "Ingreso",        color: "#10b981", icon: TrendingUp,   route: "/ingresos" },
  expense:           { label: "Egreso",         color: "#ef4444", icon: TrendingDown, route: "/gastos" },
  note:              { label: "Nota",           color: "#f59e0b", icon: StickyNote,   route: "/notas" },
  reminder:          { label: "Recordatorio",   color: "#f59e0b", icon: Clock,        route: "/notas" },
  project_start:     { label: "Proyecto: inicio",   color: "#3b82f6", icon: Briefcase, route: (e) => `/proyectos/${e.ref?.project_id || ""}` },
  project_deadline:  { label: "Proyecto: entrega", color: "#a855f7", icon: Briefcase, route: (e) => `/proyectos/${e.ref?.project_id || ""}` },
  project_completed: { label: "Proyecto: completado", color: "#22c55e", icon: CheckCircle2, route: (e) => `/proyectos/${e.ref?.project_id || ""}` },
  invoice_due:       { label: "Factura por cobrar", color: "#eab308", icon: Receipt, route: "/facturacion/enviadas" },
  invoice_paid:      { label: "Factura cobrada", color: "#10b981", icon: Receipt, route: "/facturacion/enviadas" },
  payroll:           { label: "Nómina",         color: "#a855f7", icon: Users,        route: "/nomina" },
  tax:               { label: "SAT",            color: "#1e293b", icon: CalendarIcon, route: "/calendario" },
};

const FILTER_GROUPS = [
  { key: "income",           label: "Ingresos",   dot: "#10b981" },
  { key: "expense",          label: "Egresos",    dot: "#ef4444" },
  { key: "project",          label: "Proyectos",  dot: "#3b82f6", includes: ["project_start", "project_deadline", "project_completed"] },
  { key: "note",             label: "Notas",      dot: "#f59e0b", includes: ["note", "reminder"] },
  { key: "invoice",          label: "Facturas",   dot: "#eab308", includes: ["invoice_due", "invoice_paid"] },
  { key: "tax",              label: "SAT",        dot: "#1e293b" },
  { key: "payroll",          label: "Nómina",     dot: "#a855f7" },
];

const money = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 });
const money2 = new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 2 });

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function firstOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function lastOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }

/** Return 42 dates (6 weeks) starting Monday for the given month. */
function buildMonthGrid(cursor) {
  const first = firstOfMonth(cursor);
  // Monday-first offset: JS getDay() 0=Sunday..6=Saturday
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first); start.setDate(first.getDate() - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start); d.setDate(start.getDate() + i);
    return d;
  });
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const [cursor, setCursor] = useState(() => firstOfMonth(new Date()));
  const [selected, setSelected] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [summary, setSummary] = useState(null);
  const [insights, setInsights] = useState([]);
  const [enabledFilters, setEnabledFilters] = useState(() => new Set(FILTER_GROUPS.map((g) => g.key)));
  const [query, setQuery] = useState("");
  const [detailEvent, setDetailEvent] = useState(null);
  const [quickActionDate, setQuickActionDate] = useState(null);
  const [showIncomeModal, setShowIncomeModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const monthStart = useMemo(() => toISO(firstOfMonth(cursor)), [cursor]);
  const monthEnd = useMemo(() => toISO(lastOfMonth(cursor)), [cursor]);

  const loadMonth = useCallback(async () => {
    setLoading(true);
    try {
      const [ev, sum, ins] = await Promise.all([
        api.get("/calendar/events", { params: { start: monthStart, end: monthEnd }, withCredentials: true }),
        api.get("/calendar/summary", { params: { year: cursor.getFullYear(), month: cursor.getMonth() + 1 }, withCredentials: true }),
        api.get("/calendar/insights", { params: { year: cursor.getFullYear(), month: cursor.getMonth() + 1 }, withCredentials: true }),
      ]);
      setEvents(ev.data || []);
      setSummary(sum.data || null);
      setInsights(ins.data?.insights || []);
    } catch (e) {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [monthStart, monthEnd, cursor]);

  const loadUpcoming = useCallback(async () => {
    try {
      const r = await api.get("/calendar/upcoming", { params: { days: 30 }, withCredentials: true });
      setUpcoming(r.data?.events || []);
    } catch { setUpcoming([]); }
  }, []);

  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => { loadUpcoming(); }, [loadUpcoming]);

  // Keep selected date visible when jumping months
  useEffect(() => {
    if (selected.getMonth() !== cursor.getMonth() || selected.getFullYear() !== cursor.getFullYear()) {
      setSelected(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor]);

  const isKindEnabled = useCallback((kind) => {
    for (const group of FILTER_GROUPS) {
      const kinds = group.includes || [group.key];
      if (kinds.includes(kind)) return enabledFilters.has(group.key);
    }
    return true;
  }, [enabledFilters]);

  const toggleFilter = (key) => {
    const next = new Set(enabledFilters);
    if (next.has(key)) next.delete(key); else next.add(key);
    setEnabledFilters(next);
  };
  const toggleAll = () => {
    if (enabledFilters.size === FILTER_GROUPS.length) setEnabledFilters(new Set());
    else setEnabledFilters(new Set(FILTER_GROUPS.map((g) => g.key)));
  };

  // Apply filter + search
  const term = query.trim().toLowerCase();
  const filtered = useMemo(() => events.filter((e) => {
    if (!isKindEnabled(e.kind)) return false;
    if (!term) return true;
    return [e.title, e.subtitle, String(e.amount || ""), e.date].some((v) => (v || "").toLowerCase().includes(term));
  }), [events, isKindEnabled, term]);

  const searchResults = useMemo(() => {
    if (!term) return [];
    return filtered.slice(0, 8);
  }, [filtered, term]);

  const monthGrid = useMemo(() => buildMonthGrid(cursor), [cursor]);

  // Group events by ISO date for grid dots + agenda
  const byDate = useMemo(() => {
    const map = new Map();
    for (const e of filtered) {
      const arr = map.get(e.date) || [];
      arr.push(e);
      map.set(e.date, arr);
    }
    return map;
  }, [filtered]);

  const selectedISO = toISO(selected);
  const selectedEvents = byDate.get(selectedISO) || [];

  // Charts data
  const chartMonthlyTrend = useMemo(() => {
    const days = new Map();
    for (const e of filtered) {
      const day = e.date;
      const cur = days.get(day) || { day, income: 0, expense: 0 };
      if (e.kind === "income") cur.income += Number(e.amount || 0);
      if (e.kind === "expense") cur.expense += Number(e.amount || 0);
      days.set(day, cur);
    }
    return Array.from(days.values()).sort((a, b) => (a.day < b.day ? -1 : 1))
      .map((r) => ({ ...r, day: r.day.slice(-2), net: r.income - r.expense }));
  }, [filtered]);

  const chartExpenseCategories = useMemo(() => {
    const cats = {};
    for (const e of filtered) {
      if (e.kind !== "expense") continue;
      const k = e.ref?.category || "Otras";
      cats[k] = (cats[k] || 0) + Number(e.amount || 0);
    }
    return Object.entries(cats)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [filtered]);

  const chartIncomeByClient = useMemo(() => {
    const cli = {};
    for (const e of filtered) {
      if (e.kind !== "income") continue;
      const k = e.subtitle || "Sin cliente";
      cli[k] = (cli[k] || 0) + Number(e.amount || 0);
    }
    return Object.entries(cli)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  }, [filtered]);

  const shift = (delta) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));

  const openEvent = (e) => setDetailEvent(e);
  const goToSource = (e) => {
    const meta = KIND_META[e.kind]; if (!meta) return;
    const route = typeof meta.route === "function" ? meta.route(e) : meta.route;
    setDetailEvent(null);
    navigate(route);
  };

  const openQuickActions = (dateISO) => setQuickActionDate(dateISO);

  const monthLabel = `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;

  return (
    <AppShell>
      <div className="page calendar-command" data-testid="calendar-command-center">
        <header className="cc-head">
          <div>
            <small className="eyebrow">CENTRO DE CONTROL</small>
            <h1>Todo tu negocio, <em>en una vista.</em></h1>
          </div>
          <label className="cc-search" data-testid="calendar-search">
            <Search size={16} />
            <input
              placeholder="Buscar clientes, proyectos, montos, notas…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="calendar-search-input"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Limpiar búsqueda"><X size={14} /></button>
            )}
            {term && searchResults.length > 0 && (
              <div className="cc-search-results" data-testid="calendar-search-results">
                {searchResults.map((r) => (
                  <button key={r.id} type="button" onClick={() => {
                    const d = new Date(r.date);
                    setCursor(firstOfMonth(d));
                    setSelected(d);
                    openEvent(r);
                    setQuery("");
                  }}>
                    <span className="cc-dot" style={{ background: KIND_META[r.kind]?.color }} />
                    <div>
                      <b>{r.title}</b>
                      <small>{r.date} · {KIND_META[r.kind]?.label}</small>
                    </div>
                    {r.amount != null && <em>{money2.format(r.amount)}</em>}
                  </button>
                ))}
              </div>
            )}
          </label>
        </header>

        {/* Summary Cards */}
        <section className="cc-summary" data-testid="calendar-summary-cards">
          <SummaryCard label="Ingresos" value={money.format(summary?.total_income || 0)} tone="income" testid="summary-income" />
          <SummaryCard label="Egresos" value={money.format(summary?.total_expenses || 0)} tone="expense" testid="summary-expense" />
          <SummaryCard label="Utilidad neta" value={money.format(summary?.net_profit || 0)} tone={(summary?.net_profit || 0) >= 0 ? "positive" : "negative"} testid="summary-net" />
          <SummaryCard label="Facturas cobradas" value={String(summary?.invoices_paid || 0)} testid="summary-inv-paid" />
          <SummaryCard label="Facturas por cobrar" value={String(summary?.invoices_due || 0)} sub={summary?.invoices_pending_amount ? money.format(summary.invoices_pending_amount) : ""} testid="summary-inv-due" />
          <SummaryCard label="Proyectos activos" value={String(summary?.active_projects || 0)} testid="summary-projects" />
          <SummaryCard label="Vencimientos SAT" value={String(summary?.tax_deadlines || 0)} testid="summary-tax" />
          <SummaryCard label="Total eventos" value={String(summary?.event_count || 0)} testid="summary-events" />
        </section>

        {/* Filter chips */}
        <section className="cc-filters" data-testid="calendar-filters">
          <button
            type="button"
            className={enabledFilters.size === FILTER_GROUPS.length ? "cc-filter-chip active" : "cc-filter-chip"}
            onClick={toggleAll}
            data-testid="filter-all"
          >
            <Filter size={13} /> {enabledFilters.size === FILTER_GROUPS.length ? "Todo" : "Ninguno"}
          </button>
          {FILTER_GROUPS.map((g) => (
            <button
              type="button"
              key={g.key}
              className={enabledFilters.has(g.key) ? "cc-filter-chip active" : "cc-filter-chip"}
              onClick={() => toggleFilter(g.key)}
              data-testid={`filter-${g.key}`}
            >
              <span className="cc-dot" style={{ background: g.dot }} />
              {g.label}
            </button>
          ))}
        </section>

        <div className="cc-grid">
          {/* Left: Calendar + Agenda + Charts */}
          <div className="cc-main">
            <section className="cc-calendar" data-testid="calendar-grid">
              <header>
                <button onClick={() => shift(-1)} data-testid="cal-prev-month" aria-label="Mes anterior"><ChevronLeft size={18} /></button>
                <h2 data-testid="calendar-month-label">{monthLabel}</h2>
                <button onClick={() => shift(1)} data-testid="cal-next-month" aria-label="Mes siguiente"><ChevronRight size={18} /></button>
              </header>
              <div className="cc-weekdays">
                {WEEKDAYS.map((w) => <span key={w}>{w}</span>)}
              </div>
              <div className={`cc-days ${loading ? "loading" : ""}`}>
                {monthGrid.map((d) => {
                  const iso = toISO(d);
                  const inMonth = d.getMonth() === cursor.getMonth();
                  const isSelected = iso === selectedISO;
                  const isToday = iso === toISO(new Date());
                  const dayEvents = byDate.get(iso) || [];
                  return (
                    <button
                      type="button"
                      key={iso}
                      className={`cc-day${inMonth ? "" : " outside"}${isSelected ? " selected" : ""}${isToday ? " today" : ""}`}
                      disabled={loading}
                      onClick={() => {
                        if (loading) return;
                        setSelected(d);
                        if (dayEvents.length === 0 && inMonth) openQuickActions(iso);
                      }}
                      data-testid={`cal-day-${iso}`}
                    >
                      <b>{d.getDate()}</b>
                      <div className="cc-day-dots">
                        {dayEvents.slice(0, 4).map((e, idx) => (
                          <span key={idx} className="cc-dot" style={{ background: KIND_META[e.kind]?.color }} />
                        ))}
                        {dayEvents.length > 4 && <small>+{dayEvents.length - 4}</small>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            <section className="cc-agenda" data-testid="calendar-agenda">
              <header>
                <div>
                  <small className="eyebrow">AGENDA</small>
                  <h2>{selected.toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}</h2>
                </div>
                <span className="cc-count">{selectedEvents.length} evento{selectedEvents.length === 1 ? "" : "s"}</span>
              </header>
              {selectedEvents.length === 0 ? (
                <div className="cc-empty" data-testid="agenda-empty">
                  <p>Día tranquilo. ¿Quieres registrar algo?</p>
                  <div className="cc-quick-inline">
                    <button onClick={() => setShowIncomeModal(true)} data-testid="quick-add-income"><Plus size={14} /> Ingreso</button>
                    <button onClick={() => setShowExpenseModal(true)} data-testid="quick-add-expense"><Plus size={14} /> Egreso</button>
                    <button onClick={() => navigate("/notas")} data-testid="quick-add-note"><Plus size={14} /> Nota</button>
                  </div>
                </div>
              ) : (
                <ul className="cc-agenda-list">
                  {selectedEvents.map((e) => {
                    const Icon = KIND_META[e.kind]?.icon || CalendarIcon;
                    return (
                      <li key={e.id} className="cc-agenda-item" onClick={() => openEvent(e)} data-testid={`agenda-item-${e.kind}`}>
                        <div className="cc-agenda-icon" style={{ background: `${KIND_META[e.kind]?.color}22`, color: KIND_META[e.kind]?.color }}>
                          <Icon size={16} />
                        </div>
                        <div className="cc-agenda-body">
                          <b>{e.title}</b>
                          {e.subtitle && <small>{e.subtitle}</small>}
                        </div>
                        {e.amount != null && (
                          <strong className={e.kind === "expense" ? "cc-neg" : "cc-pos"}>
                            {e.kind === "expense" ? "-" : ""}{money2.format(Math.abs(e.amount))}
                          </strong>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Charts */}
            <section className="cc-charts" data-testid="calendar-charts">
              <article className="cc-chart">
                <h3>Ingresos vs Egresos · {monthLabel}</h3>
                {chartMonthlyTrend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={chartMonthlyTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="day" stroke="#94a3b8" fontSize={11} />
                      <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => money2.format(v)} />
                      <Line type="monotone" dataKey="income" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="expense" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="net" stroke="#4169e1" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : <p className="cc-chart-empty">Sin movimientos este mes.</p>}
              </article>

              <article className="cc-chart">
                <h3>Egresos por categoría</h3>
                {chartExpenseCategories.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={chartExpenseCategories} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                        {chartExpenseCategories.map((_, i) => (
                          <Cell key={i} fill={["#4169e1", "#10b981", "#f59e0b", "#ef4444", "#a855f7", "#0ea5e9", "#f97316", "#22c55e"][i % 8]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => money2.format(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="cc-chart-empty">Sin egresos registrados.</p>}
              </article>

              <article className="cc-chart cc-chart-full">
                <h3>Top clientes del mes</h3>
                {chartIncomeByClient.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chartIncomeByClient} layout="vertical" margin={{ left: 60 }}>
                      <XAxis type="number" stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={100} />
                      <Tooltip formatter={(v) => money2.format(v)} />
                      <Bar dataKey="value" fill="#10b981" radius={[0, 8, 8, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="cc-chart-empty">Sin ingresos con cliente asignado.</p>}
              </article>
            </section>

            {/* Insights */}
            {insights.length > 0 && (
              <section className="cc-insights" data-testid="calendar-insights">
                <header><Sparkles size={16} /><h3>Insights inteligentes</h3></header>
                <ul>
                  {insights.map((ins, i) => (
                    <li key={i} className={`cc-ins cc-ins-${ins.tone}`} data-testid={`insight-${ins.tone}`}>{ins.text}</li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          {/* Right: Upcoming timeline */}
          <aside className="cc-upcoming" data-testid="calendar-upcoming">
            <header>
              <small className="eyebrow">PRÓXIMOS 30 DÍAS</small>
              <h3>Lo que viene</h3>
            </header>
            {upcoming.length === 0 ? (
              <p className="cc-empty-side">No hay eventos programados.</p>
            ) : (
              <ul>
                {upcoming.slice(0, 15).map((e) => {
                  const Icon = KIND_META[e.kind]?.icon || CalendarIcon;
                  const d = new Date(e.date);
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
                  const rel = diff === 0 ? "Hoy" : diff === 1 ? "Mañana"
                    : diff < 7 ? d.toLocaleDateString("es-MX", { weekday: "long" })
                    : d.toLocaleDateString("es-MX", { day: "numeric", month: "short" });
                  return (
                    <li key={e.id} onClick={() => openEvent(e)} data-testid="upcoming-item">
                      <div className="cc-up-date"><span>{rel}</span></div>
                      <div className="cc-up-body">
                        <div className="cc-up-icon" style={{ color: KIND_META[e.kind]?.color }}><Icon size={14} /></div>
                        <div>
                          <b>{e.title}</b>
                          {e.subtitle && <small>{e.subtitle}</small>}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </aside>
        </div>

        {/* Event detail modal */}
        {detailEvent && (
          <EventDetailModal event={detailEvent} onClose={() => setDetailEvent(null)} onGoTo={goToSource} />
        )}

        {/* Quick actions floating menu */}
        {quickActionDate && (
          <div className="cc-quick-overlay" onClick={() => setQuickActionDate(null)}>
            <div className="cc-quick-menu" onClick={(e) => e.stopPropagation()} data-testid="calendar-quick-menu">
              <header>
                <small>Acciones rápidas · {new Date(quickActionDate).toLocaleDateString("es-MX", { day: "numeric", month: "long" })}</small>
                <button onClick={() => setQuickActionDate(null)}><X size={16} /></button>
              </header>
              <button onClick={() => { setQuickActionDate(null); setShowIncomeModal(true); }} data-testid="quick-income"><TrendingUp size={16} color="#10b981" /> Añadir ingreso</button>
              <button onClick={() => { setQuickActionDate(null); setShowExpenseModal(true); }} data-testid="quick-expense"><TrendingDown size={16} color="#ef4444" /> Añadir egreso</button>
              <button onClick={() => { setQuickActionDate(null); navigate("/notas"); }} data-testid="quick-note"><StickyNote size={16} color="#f59e0b" /> Crear nota</button>
              <button onClick={() => { setQuickActionDate(null); navigate("/proyectos"); }} data-testid="quick-project"><Briefcase size={16} color="#3b82f6" /> Crear proyecto</button>
              <button onClick={() => { setQuickActionDate(null); navigate("/facturacion"); }} data-testid="quick-invoice"><Receipt size={16} color="#eab308" /> Crear factura</button>
            </div>
          </div>
        )}

        <IncomeFormModal
          open={showIncomeModal}
          onClose={() => setShowIncomeModal(false)}
          onSaved={() => { loadMonth(); loadUpcoming(); }}
          testidScope="calendar-income"
        />
        <ExpenseFormModal
          open={showExpenseModal}
          onClose={() => setShowExpenseModal(false)}
          onSaved={() => { loadMonth(); loadUpcoming(); }}
          testidScope="calendar-expense"
        />
      </div>
    </AppShell>
  );
}

function SummaryCard({ label, value, sub, tone, testid }) {
  return (
    <article className={`cc-card cc-card-${tone || "default"}`} data-testid={testid}>
      <small>{label}</small>
      <strong>{value}</strong>
      {sub && <span>{sub}</span>}
    </article>
  );
}

function EventDetailModal({ event, onClose, onGoTo }) {
  const meta = KIND_META[event.kind] || {};
  const Icon = meta.icon || CalendarIcon;
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="cc-modal-overlay" onClick={onClose} data-testid="event-detail-modal">
      <div className="cc-modal" onClick={(e) => e.stopPropagation()}>
        <header>
          <div className="cc-modal-icon" style={{ background: `${meta.color}22`, color: meta.color }}>
            <Icon size={20} />
          </div>
          <div>
            <small>{meta.label}</small>
            <h2>{event.title}</h2>
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="cc-modal-close"><X size={18} /></button>
        </header>
        <div className="cc-modal-body">
          <div className="cc-modal-row"><span>Fecha</span><b>{new Date(event.date).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}</b></div>
          {event.amount != null && (
            <div className="cc-modal-row"><span>Monto</span><b className={event.kind === "expense" ? "cc-neg" : "cc-pos"}>{event.kind === "expense" ? "-" : ""}{money2.format(Math.abs(event.amount))}</b></div>
          )}
          {event.subtitle && (
            <div className="cc-modal-row"><span>Detalle</span><b>{event.subtitle}</b></div>
          )}
          {event.ref?.category && (
            <div className="cc-modal-row"><span>Categoría</span><b>{event.ref.category}</b></div>
          )}
          {event.ref?.payment_method && (
            <div className="cc-modal-row"><span>Método de pago</span><b>{event.ref.payment_method}</b></div>
          )}
          {event.ref?.project_name && (
            <div className="cc-modal-row"><span>Proyecto</span><b>{event.ref.project_name}</b></div>
          )}
          {event.ref?.payment_status && (
            <div className="cc-modal-row"><span>Estado</span><b>{event.ref.payment_status === "cobrado" ? "Cobrado" : "Por cobrar"}</b></div>
          )}
          {event.ref?.status && event.kind !== "income" && (
            <div className="cc-modal-row"><span>Estado</span><b>{event.ref.status}</b></div>
          )}
          {event.ref?.note && (
            <div className="cc-modal-row"><span>Nota</span><b>{event.ref.note}</b></div>
          )}
        </div>
        <footer>
          <button onClick={onClose} className="cc-btn ghost">Cerrar</button>
          <button onClick={() => onGoTo(event)} className="cc-btn primary" data-testid="event-goto-source">Ver detalle completo</button>
        </footer>
      </div>
    </div>
  );
}
